import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Check, Crown, Rocket, Mail, LifeBuoy } from "lucide-react";

export default function ConfiguracionPage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    nif: "",
    address: "",
    postal_code: "",
    city: "",
    province: "",
    phone: "",
    iban: "",
    gestoria_email: "",
    default_iva: 21,
    default_irpf: 15,
    invoice_series: "F",
  });
  const [saving, setSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        business_name: user.business_name || "",
        nif: user.nif || "",
        address: user.address || "",
        postal_code: user.postal_code || "",
        city: user.city || "",
        province: user.province || "",
        phone: user.phone || "",
        iban: user.iban || "",
        gestoria_email: user.gestoria_email || "",
        default_iva: user.default_iva ?? 21,
        default_irpf: user.default_irpf ?? 15,
        invoice_series: user.invoice_series || "F",
      });
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me", {
        ...form,
        default_iva: Number(form.default_iva),
        default_irpf: Number(form.default_irpf),
      });
      await refresh();
      toast.success("Datos guardados");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upgrade = async (lookup) => {
    setUpgrading(lookup);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e));
      setUpgrading(null);
    }
  };

  const plan = user?.subscription_plan || "free";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="configuracion-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
          Configuración
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Tus datos fiscales, plantillas y suscripción.
        </p>
      </div>

      {/* Plan card */}
      <Card className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-600 grid place-items-center">
              {plan === "business" ? (
                <Crown className="w-6 h-6" />
              ) : plan === "pro" ? (
                <Sparkles className="w-6 h-6" />
              ) : (
                <Rocket className="w-6 h-6" />
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">
                Plan actual
              </div>
              <div className="font-display font-bold text-2xl">
                {plan === "free" ? "Gratis" : plan === "pro" ? "Pro" : "Business"}
              </div>
              <div className="text-sm text-slate-300 mt-1">
                {plan === "free"
                  ? "5 facturas al mes · Actualiza para ilimitado"
                  : plan === "pro"
                    ? "Facturas ilimitadas + envío a gestoría"
                    : "Todo incluido + cobros online (próximamente)"}
              </div>
            </div>
          </div>
          {plan !== "business" && (
            <div className="flex gap-2">
              {plan === "free" && (
                <Button
                  onClick={() => upgrade("pro_monthly")}
                  disabled={upgrading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="upgrade-pro-btn"
                >
                  {upgrading === "pro_monthly" ? "Redirigiendo..." : "Pasar a Pro · 9,99€/mes"}
                </Button>
              )}
              <Button
                onClick={() => upgrade("business_monthly")}
                disabled={upgrading}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 hover:text-white bg-transparent"
                data-testid="upgrade-business-btn"
              >
                {upgrading === "business_monthly" ? "Redirigiendo..." : "Pasar a Business · 19,99€/mes"}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Datos fiscales */}
      <Card className="p-6">
        <h2 className="font-display font-bold text-xl mb-4">Datos fiscales</h2>
        <p className="text-sm text-slate-500 mb-4">
          Aparecerán como emisor en tus facturas y presupuestos.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Tu nombre</Label>
            <Input value={form.name} onChange={set("name")} data-testid="cfg-name" />
          </div>
          <div>
            <Label>Nombre comercial / Razón social</Label>
            <Input
              value={form.business_name}
              onChange={set("business_name")}
              data-testid="cfg-business-name"
              placeholder="Ej: María López Servicios"
            />
          </div>
          <div>
            <Label>NIF</Label>
            <Input value={form.nif} onChange={set("nif")} data-testid="cfg-nif" />
          </div>
          <div>
            <Label>Teléfono</Label>
            <Input value={form.phone} onChange={set("phone")} />
          </div>
          <div className="md:col-span-2">
            <Label>Dirección fiscal</Label>
            <Input value={form.address} onChange={set("address")} />
          </div>
          <div>
            <Label>Código postal</Label>
            <Input value={form.postal_code} onChange={set("postal_code")} />
          </div>
          <div>
            <Label>Ciudad</Label>
            <Input value={form.city} onChange={set("city")} />
          </div>
          <div>
            <Label>Provincia</Label>
            <Input value={form.province} onChange={set("province")} />
          </div>
          <div>
            <Label>IBAN (aparece en el PDF)</Label>
            <Input value={form.iban} onChange={set("iban")} placeholder="ES00 0000 ..." />
          </div>
        </div>
      </Card>

      {/* Gestoría */}
      <Card className="p-6">
        <h2 className="font-display font-bold text-xl mb-2">Gestoría</h2>
        <p className="text-sm text-slate-500 mb-4">
          Configura una vez el email de tu gestoría y cada factura emitida se puede enviar automáticamente con PDF + XML Facturae.
        </p>
        <div>
          <Label>Email de la gestoría</Label>
          <Input
            type="email"
            value={form.gestoria_email}
            onChange={set("gestoria_email")}
            placeholder="gestoria@ejemplo.com"
            data-testid="cfg-gestoria-email"
          />
        </div>
      </Card>

      {/* Impuestos */}
      <Card className="p-6">
        <h2 className="font-display font-bold text-xl mb-4">Preferencias de facturación</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>IVA por defecto (%)</Label>
            <Input
              type="number"
              step="0.1"
              value={form.default_iva}
              onChange={set("default_iva")}
              data-testid="cfg-default-iva"
            />
          </div>
          <div>
            <Label>IRPF por defecto (%)</Label>
            <Input
              type="number"
              step="0.1"
              value={form.default_irpf}
              onChange={set("default_irpf")}
              data-testid="cfg-default-irpf"
            />
          </div>
          <div>
            <Label>Serie de factura</Label>
            <Input value={form.invoice_series} onChange={set("invoice_series")} />
          </div>
        </div>
      </Card>

      {/* Contacto / Soporte */}
      <Card className="p-6 border-blue-100 bg-blue-50/40">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-blue-600 grid place-items-center shrink-0">
            <LifeBuoy className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-xl">¿Necesitas ayuda?</h2>
            <p className="text-sm text-slate-600 mt-1">
              Escríbenos y te respondemos personalmente en menos de 24 h.
            </p>
            <a
              href="mailto:info@autonomopro.online"
              className="inline-flex items-center gap-2 mt-3 text-blue-600 hover:text-blue-700 font-medium text-sm"
              data-testid="config-support-email"
            >
              <Mail className="w-4 h-4" />
              info@autonomopro.online
            </a>
          </div>
        </div>
      </Card>

      <div className="flex justify-end sticky bottom-4">
        <Button
          onClick={save}
          disabled={saving}
          size="lg"
          className="bg-blue-600 hover:bg-blue-700 shadow-lg"
          data-testid="cfg-save-btn"
        >
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </div>
    </div>
  );
}
